import {computed, ref} from "vue";
import {api, routes} from "@/plugins/axios";

const memberFeatures = ref(null);
const loading = ref(false);
let loadingPromise = null;

export function useCheckFeature() {
    const getMemberFeatures = (organizationSlug) => {
        return api.get(routes.members.features(organizationSlug))
            .then((response) => {
                return Promise.resolve(response.data);
            }).catch((err) => {
                return Promise.reject(err);
            });
    }

    const loadMemberFeatures = (slug) => {
        if (memberFeatures.value) {
            return Promise.resolve(memberFeatures.value);
        }

        if (loadingPromise) {
            return loadingPromise;
        }

        loading.value = true;

        loadingPromise = getMemberFeatures(slug).then((response) => {
            memberFeatures.value = response;
            return response;
        }).finally(() => {
            loading.value = false;
            loadingPromise = null;
        });

        return loadingPromise;
    }

    const clearMemberFeatures = () => {
        memberFeatures.value = null;
        loadingPromise = null;
        loading.value = false;
    }

    const isChatFeatureActive = computed(() => {
        if(!memberFeatures.value) {
            return false;
        }
        return Object.hasOwn(memberFeatures.value, 'chat') && memberFeatures.value.chat;
    });

    const benefitsFeature = computed(() => {
        if(!memberFeatures.value || !Object.hasOwn(memberFeatures.value, 'benefits')) {
            return null;
        }
        return memberFeatures.value.benefits;
    });

    const benefitsTier = computed(() => {
        return benefitsFeature.value?.tier ?? 'off';
    });

    const isBenefitsFeatureActive = computed(() => {
        return benefitsTier.value !== 'off';
    });

    const isBenefitsFeaturePremium = computed(() => {
        return benefitsTier.value === 'premium';
    });

    const canAccessBenefitDefinitions = computed(() => {
        return benefitsFeature.value?.definitions?.view ?? false;
    });

    const canManageBenefitDefinitions = computed(() => {
        return benefitsFeature.value?.definitions?.manage ?? false;
    });

    // Life surfaces (Life Policies, Enrollments Dashboard) are gated on the life
    // capability, NOT the health definitions flag. manageable_kinds comes from the
    // /members/features payload (BenefitsCapabilityResolver::forGlobal).
    const canAccessLifePolicies = computed(() => {
        return (benefitsFeature.value?.manageable_kinds ?? []).includes('life');
    });

    // Mirrors the api's canExportBeneficiaries (permission AND, once configured, view
    // visibility) via the /members/features payload — not client-side role calculus.
    const canExportBeneficiaries = computed(() => {
        return benefitsFeature.value?.beneficiaries?.export ?? false;
    });

    const canExportPedigree = computed(() => {
        return memberFeatures.value?.pedigree?.export ?? false;
    });

    return {
        memberFeatures,
        getMemberFeatures,
        loadMemberFeatures,
        clearMemberFeatures,
        isChatFeatureActive,
        benefitsFeature,
        benefitsTier,
        isBenefitsFeatureActive,
        isBenefitsFeaturePremium,
        canAccessBenefitDefinitions,
        canManageBenefitDefinitions,
        canAccessLifePolicies,
        canExportBeneficiaries,
        canExportPedigree,
    }
}
